import { useEffect, useRef } from 'react';

/**
 * Custom hook that detects clicks outside of a provided container
 * and triggers a callback function
 *
 * @param {Function} callback - Function to call when click outside is detected
 * @param {boolean} enabled - Whether the hook should be active (default: true)
 * @returns {React.RefObject} ref to attach to the container element
 *
 * @example
 * const ref = useClickOutside(() => setIsOpen(false));
 * return <div ref={ref}>Content</div>;
 */
export const useClickOutside = (callback: (event: Event) => void, enabled = true) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!enabled) return;

        const handleClickOutside = (event: Event) => {
            // Check if the click target is outside the container
            if (ref.current && !ref.current.contains(event.target as Node)) {
                callback(event);
            }
        };

        // Add event listener for mousedown (more reliable than click)
        document.addEventListener('mousedown', handleClickOutside);

        // Also listen for touchstart for mobile devices
        document.addEventListener('touchstart', handleClickOutside);

        // Cleanup function to remove event listeners
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [callback, enabled]);

    return ref;
};
